/**
 * Platform secrets — the credentials Clavis reads from each service's
 * ENVIRONMENT (Railway → service → Variables), as opposed to the per-studio
 * `studio_secrets` rows in `secret-kinds.ts` (vendor-credentials-truth,
 * 2026-10-22).
 *
 * Why this list exists. After the Railway security incident of 2026-10-21
 * Mario asked whether rotating "all the API keys" was a matter of the
 * dashboard's Vendor credentials page. It is not: that page writes
 * `studio_secrets`, and a second, larger set of credentials is read only
 * from the environment — database URLs, Supabase keys, Inngest keys, the
 * Anthropic key, the HMAC secrets, the Telegram bot token… — and a new
 * value there does nothing until the service is redeployed. Nothing in the
 * repo named that set, so the page could not say what it did NOT cover. This
 * module is that statement, in one place, for the page to render and for a
 * test to hold against the code:
 *
 *   - `PLATFORM_SECRETS`   — every environment variable a production service
 *                             reads that carries a credential (and the public
 *                             halves that travel with one). `services` is
 *                             where Railway held the variable on 2026-10-22;
 *                             `overriddenBy` names the `studio_secrets` row
 *                             that wins over the variable for a studio.
 *   - `RETIRED_ENV_NAMES`  — names phase 48 stopped reading but never removed
 *                             from Railway. The incident exposed those copies
 *                             too: several were EQUAL to the live rows. Delete
 *                             them; rotating them changes nothing.
 *
 * The gate (`packages/canonical-data/tests/secret-kinds.test.ts`) walks every
 * `src/` file under `apps/` and `packages/` for a secret-shaped environment
 * name (`SECRET_SHAPED_ENV_NAME`) the code reads or declares, and fails when
 * one is in neither list — so a new `LAYER_*_SECRET` cannot appear in a
 * bootstrap without being named here, and a retired name cannot quietly come
 * back into a bootstrap. Prose (comments, error messages) does not count: the
 * scan looks at code shapes only. Per-service placement is NOT derivable from
 * the code (shared packages read on behalf of whichever app imports them), so
 * `services` is operator knowledge — check `railway variables` before a
 * rotation and correct the entry if it drifted.
 *
 * Standalone module: no imports beyond the kind union, so the dashboard SPA
 * can alias it like `secret-kinds.ts` (`apps/dashboard/web/vite.config.ts`).
 */

import type { KnownSecretKind } from "./secret-kinds.js";

/** The Railway services (plus the operator-only CLI) a variable is set on. */
export type ClavisService = "webhook-receiver" | "dashboard" | "mcp-server" | "cli";

export interface PlatformSecret {
  /** The environment variable, exactly as Railway spells it. */
  readonly name: string;
  /** Where the variable is set. Operator knowledge, see the module comment. */
  readonly services: ReadonlyArray<ClavisService>;
  /**
   * `public` marks the non-secret half of a pair (a publishable key, a public
   * key) that rotates together with its secret and is listed so the pair is
   * complete.
   */
  readonly sensitivity: "secret" | "public";
  /**
   * The `studio_secrets` kind whose row wins over this variable for its
   * studio. Present only for the env FALLBACKS (`KNOWN_SECRET_KIND_META[kind]
   * .envFallback` names this variable back).
   */
  readonly overriddenBy?: KnownSecretKind;
  /** What reads it, in one or two sentences. */
  readonly what: string;
  /** How to rotate it and what breaks, in one to three sentences. */
  readonly rotation: string;
}

const ALL: ReadonlyArray<ClavisService> = [
  "webhook-receiver",
  "dashboard",
  "mcp-server",
];

export const PLATFORM_SECRETS: ReadonlyArray<PlatformSecret> = [
  {
    name: "LAYER_RLS_TEST_DB_URL",
    services: [...ALL, "cli"],
    sensitivity: "secret",
    what: "Postgres URL of the layer_app role (the password is inside it): every per-request query runs through it under RLS.",
    rotation:
      "Change the role's password in Postgres (ALTER ROLE layer_app PASSWORD …), set the new URL on every service and in the laptop lane files, redeploy. Open connections survive the change; new ones fail until the redeploy lands, so do the two steps back to back.",
  },
  {
    name: "LAYER_RLS_TEST_ADMIN_DB_URL",
    services: [...ALL, "cli"],
    sensitivity: "secret",
    what: "Postgres URL of the postgres role (BYPASSRLS): the secret resolvers, the receiver's pre-deploy migration step and the seed scripts.",
    rotation:
      "Reset the database password in Supabase (Project Settings → Database), then set the new URL on every service at once and redeploy; the next deploy's migration step already needs it.",
  },
  {
    name: "LAYER_SUPABASE_SERVICE_ROLE_KEY",
    services: ["webhook-receiver", "dashboard"],
    sensitivity: "secret",
    what: "Supabase secret key (sb_secret_…): verifies every staff login token through GoTrue, mints member magic links, writes storage.",
    rotation:
      "Create a new secret key under Supabase → API keys, set it on both services, redeploy, THEN delete the old key. Deleting first logs every staff member out.",
  },
  {
    name: "LAYER_SUPABASE_ANON_KEY",
    services: ["dashboard"],
    sensitivity: "public",
    what: "Supabase publishable key the dashboard backend checks at boot; the SPA's copy is LAYER_PUB_SUPABASE_ANON_KEY.",
    rotation:
      "Rotates with the publishable key: new key in Supabase, set both names, redeploy.",
  },
  {
    name: "LAYER_PUB_SUPABASE_ANON_KEY",
    services: ["dashboard"],
    sensitivity: "public",
    what: "The publishable key baked into the dashboard SPA at Docker build (falls back to LAYER_SUPABASE_ANON_KEY).",
    rotation:
      "A redeploy is a rebuild, so set + redeploy is the whole procedure.",
  },
  {
    name: "INNGEST_EVENT_KEY",
    services: ALL,
    sensitivity: "secret",
    what: "Lets each service send events to Inngest Cloud — every saga start.",
    rotation:
      "Create a new event key in Inngest Cloud, set it on all three services, redeploy, then delete the old key.",
  },
  {
    name: "INNGEST_SIGNING_KEY",
    services: ALL,
    sensitivity: "secret",
    what: "Signs Inngest's calls into the receiver's /api/inngest route; the client factory requires it on every service.",
    rotation:
      "Rotate under Inngest Cloud → Signing keys: put the NEW key in INNGEST_SIGNING_KEY and the old one in INNGEST_SIGNING_KEY_FALLBACK (the SDK reads it natively), redeploy, then delete the old key and the fallback variable. Zero downtime.",
  },
  {
    name: "LAYER_ANTHROPIC_API_KEY",
    services: ALL,
    sensitivity: "secret",
    what: "The Claude API key behind the chat agent, inbox triage and every other model call.",
    rotation:
      "New key in the Anthropic console, set on all three services, redeploy (clients live for the process), then revoke the old key.",
  },
  {
    name: "LAYER_TRANSCRIPTION_API_KEY",
    services: ["webhook-receiver"],
    sensitivity: "secret",
    overriddenBy: "transcription.api_key",
    what: "Groq Whisper key for WhatsApp voice notes.",
    rotation:
      "New key at Groq, set, redeploy, revoke the old one — or store the key per studio in the transcription.api_key row and leave the variable unset.",
  },
  {
    name: "LAYER_TELEGRAM_BOT_TOKEN",
    services: ["webhook-receiver", "dashboard"],
    sensitivity: "secret",
    overriddenBy: "telegram.bot_token",
    what: "Token of the staff-alert Telegram bot: sends alerts and approval buttons; the receiver checks Telegram's webhook secret as sha256 of it.",
    rotation:
      "Regenerate in @BotFather, set, redeploy, then re-run setWebhook so the new sha256 secret is registered (recipe: smoke-tests/telegram-approve-ingress.md). Never create a telegram.bot_token row instead.",
  },
  {
    name: "LAYER_MEMBER_AUTH_HMAC_SECRET",
    services: ["webhook-receiver", "dashboard"],
    sensitivity: "secret",
    what: "Signs member sessions (180 days), member magic-link tokens, pay links (up to 366 days) and the permanent .ics calendar links. Must be identical on both services.",
    rotation:
      "A hard cut — there is no previous-key slot — so every outstanding link, code and member session dies at the redeploy. Generate 32 random bytes as base64, set on both services, redeploy together.",
  },
  {
    name: "LAYER_PAY_LINK_AGENT_HMAC_SECRET",
    services: ["mcp-server", "webhook-receiver"],
    sensitivity: "secret",
    what: "Signs pay links minted by an MCP agent, separately from the member-auth key.",
    rotation:
      "Hard cut for agent-minted links only. Set the same new value on both services and redeploy.",
  },
  {
    name: "LAYER_UNSUBSCRIBE_HMAC_SECRET",
    // On the mcp-server too since 2026-10-23, with the same value:
    // `resubscribe_newsletter`, which staff can call through the MCP
    // connector, signs its notice's one-click undo link in the process that
    // runs it, and refuses where the secret is unbound.
    services: ALL,
    sensitivity: "secret",
    what: "Signs the unsubscribe link in every marketing email, in whichever service sends it; the dashboard's /unsubscribe verifies it. Those tokens never expire.",
    rotation:
      "Hard cut: every unsubscribe link already sent returns 403 afterwards. Set the same value on all three services, redeploy.",
  },
  {
    name: "LAYER_TRAINER_PAY_HMAC_SECRET",
    services: ["dashboard"],
    sensitivity: "secret",
    what: "Signs trainers' 30-day pay-statement links.",
    rotation:
      "Hard cut for outstanding statement links. Set, redeploy, re-send a statement if a trainer asks.",
  },
  {
    name: "LAYER_CLAVIS_LEAD_LINK_KEY",
    // clavis-signup-emails (2026-11-03). In this (production) project only the
    // dashboard holds it: its /clavis-emails/add opens the links. The links are
    // minted by the SALES DEMO's webhook receiver, in the `clavis-demo` Railway
    // project, which holds the same value (that branch's divergence ledger,
    // out-of-git). Production's receiver reads the name too, but no production
    // studio turns the Add link on (`studio.clavis_emails_add_link`). Not set
    // until the Clavis emails go live, on Mario's yes; until then the page
    // answers 503 and `check-env --service dashboard` reports it missing.
    services: ["dashboard"],
    sensitivity: "secret",
    what: "Opens the Add link in the sales demo's \"New demo sign-up\" alert (`clavis-lead-links.ts`, AES-256-GCM): production's dashboard reads it at /clavis-emails/add, and the demo's receiver mints the links with the same value. The token carries the person's name and email for 30 days. 32 random bytes, base64.",
    rotation:
      "Hard cut for Add links already sent (they open a 403 page). Set the same new value on production's dashboard and the demo's webhook receiver, redeploy both. Unset on the demo's receiver, the alerts carry no Add link; unset on the dashboard, the page answers 503.",
  },
  {
    name: "LAYER_ERROR_INGEST_SECRET",
    services: ["dashboard"],
    sensitivity: "secret",
    what: "Shared secret CI presents on POST /api/errors/ingest/:slug when main goes red; the only sender is the GitHub Actions secret of the same name.",
    rotation:
      "Set the same new value on the dashboard service and in the repository's GitHub Actions secrets, redeploy.",
  },
  {
    name: "LAYER_MCP_JWT_SIGNING_KEY",
    services: ["mcp-server"],
    sensitivity: "secret",
    what: "ES256 private key (PEM) that signs MCP access tokens (one hour).",
    rotation:
      "Generate a new keypair, set both PEM variables, redeploy. Clients see one 401 and refresh silently — refresh tokens are stored hashes, not JWTs.",
  },
  {
    name: "LAYER_MCP_JWT_PUBLIC_KEY",
    services: ["mcp-server"],
    sensitivity: "public",
    what: "The matching public key; verifies MCP access tokens.",
    rotation: "Rotates with the signing key.",
  },
  {
    name: "LAYER_INSTAGRAM_ACCESS_TOKEN",
    services: ALL,
    sensitivity: "secret",
    overriddenBy: "instagram.access_token",
    what: "Fallback Graph API token, read only when the studio has no instagram.access_token row.",
    rotation:
      "Prefer the row. If the variable is set, rotate it at Meta, set, redeploy.",
  },
  {
    name: "LAYER_INSTAGRAM_BUSINESS_ACCOUNT_ID",
    services: ALL,
    sensitivity: "public",
    overriddenBy: "instagram.business_account_id",
    what: "Fallback Instagram Business account id, read only when the studio has no row.",
    rotation: "An id, not a credential; nothing to rotate.",
  },
  {
    name: "LAYER_INSTAGRAM_DM_ACCESS_TOKEN",
    services: ALL,
    sensitivity: "secret",
    overriddenBy: "instagram.dm_access_token",
    what: "Fallback Messaging API token for Instagram DMs, read only when the studio has no instagram.dm_access_token row.",
    rotation:
      "Prefer the row. If the variable is set, rotate it at Meta, set, redeploy.",
  },
  {
    name: "LAYER_INSTAGRAM_APP_SECRET",
    services: ["webhook-receiver"],
    sensitivity: "secret",
    overriddenBy: "instagram.app_secret",
    what: "Fallback Instagram app secret for the webhook HMAC, read only when neither instagram.app_secret nor whatsapp.app_secret rows exist.",
    rotation:
      "Prefer the row. If the variable is set, reset the secret in the Instagram-Login Meta app, set, redeploy.",
  },
  {
    name: "LAYER_INSTAGRAM_VERIFY_TOKEN",
    services: ["webhook-receiver"],
    sensitivity: "secret",
    overriddenBy: "instagram.verify_token",
    what: "Fallback Instagram webhook verify token, read only when the studio has no row.",
    rotation:
      "Prefer the row. If the variable is set, change it here and in the Meta app console together, redeploy.",
  },
];

export interface RetiredEnvName {
  /** The variable, as it was (and may still be) spelled on Railway. */
  readonly name: string;
  /** The `studio_secrets` kind that took over, or null when nothing did. */
  readonly replacedBy: KnownSecretKind | null;
  /** Why no deployed service reads it any more. */
  readonly note: string;
}

/**
 * Phase 48 (2026-05-28) moved every per-studio vendor credential into
 * `studio_secrets` and stopped reading these; its "step 2 — remove the
 * retired env vars from Railway" was never executed, so the copies sat
 * there until the 2026-10-21 incident. Only the connectors' exported env
 * loaders still mention the names, and no production bootstrap calls those
 * loaders — the gate's A3 case holds that line.
 */
export const RETIRED_ENV_NAMES: ReadonlyArray<RetiredEnvName> = [
  {
    name: "LAYER_SECRETS_ENCRYPTION_KEY",
    replacedBy: null,
    note: "The phase-20 AES-256-GCM key; application-layer encryption was retired by migrations 030-032 and the value column is plaintext under RLS.",
  },
  {
    name: "LAYER_STRIPE_API_KEY",
    replacedBy: "stripe.api_key",
    note: "Only the Stripe connector's exported env loader still reads it; no production bootstrap calls that loader.",
  },
  {
    name: "LAYER_STRIPE_WEBHOOK_SIGNING_SECRET",
    replacedBy: "stripe.webhook_signing_secret",
    note: "Same: the receiver verifies signatures against the two rows.",
  },
  {
    name: "LAYER_KLAVIYO_WEBHOOK_TOKEN",
    replacedBy: "klaviyo.webhook_bearer_token",
    note: "The receiver's bootstrap hard-codes an empty env token and resolves the row per delivery.",
  },
  {
    name: "LAYER_RESEND_API_KEY",
    replacedBy: "resend.api_key",
    note: "The senders build their Resend client from the row and pass the key through a synthetic env bag; the Railway variable is never read. LAYER_RESEND_FROM_ADDRESS, by contrast, IS read and must stay.",
  },
  {
    name: "LAYER_RESEND_WEBHOOK_SIGNING_SECRET",
    replacedBy: "resend.webhook_signing_secret",
    note: "The receiver's bootstrap hard-codes an empty env secret and resolves the row per delivery.",
  },
  {
    name: "LAYER_WHATSAPP_ACCESS_TOKEN",
    replacedBy: "whatsapp.access_token",
    note: "Only the WhatsApp connector's exported env loader still reads it.",
  },
  {
    name: "LAYER_WHATSAPP_APP_SECRET",
    replacedBy: "whatsapp.app_secret",
    note: "Only the WhatsApp connector's exported env loader still reads it.",
  },
  {
    name: "LAYER_WHATSAPP_PHONE_NUMBER_ID",
    replacedBy: "whatsapp.phone_number_id",
    note: "Only the WhatsApp connector's exported env loader still reads it.",
  },
  {
    name: "LAYER_WHATSAPP_VERIFY_TOKEN",
    replacedBy: "whatsapp.verify_token",
    note: "Only the WhatsApp connector's exported env loader still reads it.",
  },
  {
    name: "LAYER_HERCIE_WEBHOOK_SIGNING_SECRET",
    replacedBy: null,
    note: "The Hercie webhook exchange was deleted on 2026-09-01 together with its two kinds; nothing in the repo names this variable.",
  },
  {
    name: "LAYER_HERCIE_N8N_BEARER_TOKEN",
    replacedBy: null,
    note: "Deleted with the Hercie exchange on 2026-09-01.",
  },
];

/**
 * An environment name that carries a credential, by its suffix. The gate
 * classifies every such name the production code reads; names outside this
 * shape (`LAYER_SUPABASE_URL`, `LAYER_RESEND_FROM_ADDRESS`, the `_ID`
 * identifiers) are configuration, not secrets, and are listed above only
 * when they travel with a secret.
 */
export const SECRET_SHAPED_ENV_NAME =
  /\b(?:LAYER|INNGEST)_[A-Z0-9_]*(?:_KEY|_SECRET|_TOKEN|_PASSWORD|_DB_URL)\b/g;

export type EnvNameClass =
  | { readonly kind: "platform"; readonly entry: PlatformSecret }
  | { readonly kind: "retired"; readonly entry: RetiredEnvName };

const PLATFORM_BY_NAME: ReadonlyMap<string, PlatformSecret> = new Map(
  PLATFORM_SECRETS.map((s) => [s.name, s]),
);
const RETIRED_BY_NAME: ReadonlyMap<string, RetiredEnvName> = new Map(
  RETIRED_ENV_NAMES.map((r) => [r.name, r]),
);

/** Which list, if any, an environment name belongs to. */
export function classifyEnvName(name: string): EnvNameClass | null {
  const platform = PLATFORM_BY_NAME.get(name);
  if (platform !== undefined) return { kind: "platform", entry: platform };
  const retired = RETIRED_BY_NAME.get(name);
  if (retired !== undefined) return { kind: "retired", entry: retired };
  return null;
}

/** True when `name` is one whole secret-shaped environment name. */
export function isSecretShapedEnvName(name: string): boolean {
  return /^(?:LAYER|INNGEST)_[A-Z0-9_]*(?:_KEY|_SECRET|_TOKEN|_PASSWORD|_DB_URL)$/.test(
    name,
  );
}
